import { api } from "@/services/api";
import { FeedbackType } from "@/services/api/fetchers/feedback";
import { useBoundStore } from "@/app/store";
import { DrawerAndModal } from "@/shared/components/common/DrawerAndModal";
import { addToast, Button, Select, SelectItem, Textarea } from "@heroui/react";
import { useState } from "react";
import { useShallow } from "zustand/shallow";

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const { formValues, handleChange, resetForm, errors, validateForm } = useFeedbackForm();
  const mutateAddFeedback = api.hooks.feedback.useAddFeedback();
  const deviceType = useBoundStore(useShallow((state) => state.device.type));
  const userId = useBoundStore(useShallow((state) => state.user.id));

  const handleSendFeedback = () => {
    if (validateForm() && userId && formValues.type) {
      mutateAddFeedback
        .mutateAsync({
          user_id: userId,
          type: formValues.type,
          description: formValues.description,
        })
        .then(() => {
          addToast({
            title: "Feedback successfully sent.",
            color: "success",
          });
        });
      onClose();
      resetForm();
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
      resetForm();
    }
  };

  return (
    <DrawerAndModal
      id="feedback"
      title="Feedback"
      closeLabel="Cancel"
      confirmLabel="Send"
      onConfirm={handleSendFeedback}
      externalState={{
        isOpen,
        onOpenChange: handleOpenChange,
      }}
    >
      <div className="space-y-4">
        <Select
          errorMessage={errors.type}
          isInvalid={!!errors.type}
          label="Type"
          name="type"
          selectedKeys={formValues.type ? [formValues.type] : []}
          size="sm"
          onChange={handleChange}
        >
          {feedbackTypes.map((type) => (
            <SelectItem key={type.key}>{type.label}</SelectItem>
          ))}
        </Select>
        <Textarea
          description={`${formValues.description.length}/255`}
          errorMessage={errors.description}
          isInvalid={!!errors.description}
          label="Description"
          name="description"
          value={formValues.description}
          onChange={handleChange}
        />
        {deviceType === "mobile" && (
          <Button color="primary" fullWidth onPress={handleSendFeedback}>
            Send Feedback
          </Button>
        )}
      </div>
    </DrawerAndModal>
  );
}

function useFeedbackForm() {
  const [formValues, setFormValues] = useState<{ description: string; type: FeedbackType | null }>({
    description: "",
    type: null,
  });
  const [errors, setErrors] = useState<{ description?: string; type?: string }>({});

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined })); // Clear error on change
  };

  const validateForm = () => {
    const newErrors: { type?: string; description?: string } = {};
    if (!formValues.type) newErrors.type = "Type is required.";
    if (!formValues.description) {
      newErrors.description = "Description is required.";
    } else if (formValues.description.length > 255) {
      newErrors.description = "Description must not exceed 255 characters.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormValues({ type: null, description: "" });
    setErrors({});
  };

  return { formValues, handleChange, resetForm, errors, validateForm };
}

const feedbackTypes: { key: FeedbackType; label: string }[] = [
  { key: "feature", label: "Feature" },
  { key: "bug", label: "Bug" },
  { key: "comment", label: "Comment" },
];
